import { useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { sentryCaptureException } from '@/utils/sentry';
import type { AddFundsModalData, AddFundsResult } from '../components/add-funds-modal';
import {
  autoTopUpErrors,
  customAmountError,
  DEFAULT_AUTO_TOP_UP_SETTINGS,
  hasAutoTopUpErrors,
  immediateTopUpChargeCents,
  initialAmountSelection,
  type AmountSelection,
  type AutoTopUpSettings,
} from '../utils/add-funds';
import { firstBillingError } from '../utils/credit-card-form-validation';
import { FLEX_CREDITS_CONFIG } from '../utils/credits-purchase-config';
import { useCreditsPurchaseForm, type CreditsPurchaseFormView } from './use-credits-purchase-form';

type SubmitButton = {
  label: string;
  isDisabled: boolean;
  tooltip: string;
};

const AUTO_TOP_UP_UNAVAILABLE = "Auto top-up can't be turned on yet.";
const CARD_INCOMPLETE = 'Enter your card number, expiry date, and CVC.';

type SubmitTooltipInput = { isDisabled: boolean; isAutoTopUp: boolean; canSaveAutoTopUp: boolean; formTooltip: string };

function submitTooltip({ isDisabled, isAutoTopUp, canSaveAutoTopUp, formTooltip }: SubmitTooltipInput): string {
  if (!isDisabled) return '';
  if (isAutoTopUp) return canSaveAutoTopUp ? '' : AUTO_TOP_UP_UNAVAILABLE;
  return formTooltip;
}

function submitLabel(isAutoTopUp: boolean, selection: AmountSelection, chargeNowCents: number): string {
  if (!isAutoTopUp) return `Add funds: ${formatCentsToUsd((selection.dollars ?? 0) * 100, 0)}`;
  if (chargeNowCents > 0) return `Add funds: ${formatCentsToUsd(chargeNowCents)}`;
  return 'Turn on auto top-up';
}

function amountErrorFor(selection: AmountSelection, capError: string | undefined): string | undefined {
  const rangeError = selection.kind === 'custom' ? customAmountError(selection.dollars) : undefined;
  return rangeError ?? capError;
}

function useAutoTopUpSave(
  onSave: AddFundsModalData['onSaveAutoTopUp'],
  onConfirm: (result: AddFundsResult) => void,
  onProcessingChange: (isProcessing: boolean) => void,
) {
  const snackbars = useSnackbars();
  const [settings, setSettings] = useState<AutoTopUpSettings>(DEFAULT_AUTO_TOP_UP_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    if (onSave == null) return;
    setIsSaving(true);
    onProcessingChange(true);
    try {
      await onSave(settings);
      onConfirm({ savedCard: false });
    } catch (error) {
      sentryCaptureException(error instanceof Error ? error : new Error('Auto top-up save failed'));
      snackbars.create({
        variant: 'critical',
        id: 'add-funds.autoTopUp.error',
        message: "Auto top-up couldn't be turned on. Try again.",
      });
    } finally {
      setIsSaving(false);
      onProcessingChange(false);
    }
  };

  return {
    settings,
    setSettings,
    errors: autoTopUpErrors(settings),
    isActive: settings.isEnabled,
    canSave: onSave != null,
    isSaving,
    save,
  };
}

const BILLING_FIELDS = ['name', 'addressLine1', 'city', 'zipCode', 'country', 'state'];

function useCardEntry(form: CreditsPurchaseFormView) {
  const [isCardErrorShown, setIsCardErrorShown] = useState(false);
  const [isCardEntryOpen, setIsCardEntryOpen] = useState(false);
  return {
    cardError: isCardErrorShown && !form.isCardComplete ? CARD_INCOMPLETE : undefined,
    cardEntry: {
      isOpen: !form.useSavedCard && isCardEntryOpen,
      onOpen: () => setIsCardEntryOpen(true),
      onClose: () => setIsCardEntryOpen(false),
    },
    showMissingBillingDetails: () => {
      setIsCardEntryOpen(true);
      BILLING_FIELDS.forEach((field) => form.onFieldBlur(field));
    },
    showIncompleteCard: () => {
      setIsCardEntryOpen(true);
      setIsCardErrorShown(true);
    },
  };
}

function applySelection(form: CreditsPurchaseFormView, next: AmountSelection) {
  if (next.dollars == null) return;
  if (next.kind === 'preset') {
    form.onPackSelect(next.dollars);
  } else {
    form.onQuantityChange(next.dollars);
  }
}

type UseAddFundsFormOptions = {
  data: AddFundsModalData;
  onConfirm: (result: AddFundsResult) => void;
  onProcessingChange: (isProcessing: boolean) => void;
};

export function useAddFundsForm({ data, onConfirm, onProcessingChange }: UseAddFundsFormOptions) {
  const [selection, setSelection] = useState<AmountSelection>(() =>
    initialAmountSelection(data.initialAmountDollars, data.funding),
  );
  const autoTopUp = useAutoTopUpSave(data.onSaveAutoTopUp, onConfirm, onProcessingChange);
  const form = useCreditsPurchaseForm(FLEX_CREDITS_CONFIG, data.currentBalanceCents, {
    pageName: data.pageName,
    initialQuantity: selection.dollars ?? undefined,
    initialSaveCard: true,
    onSuccess: onConfirm,
    onProcessingChange,
  });

  const card = useCardEntry(form);
  const amountError = amountErrorFor(selection, form.quantityError);
  const isSubmitting = form.isSubmitting || autoTopUp.isSaving;
  const isDisabled = autoTopUp.isActive ? !autoTopUp.canSave || isSubmitting : form.submitBlocked;
  const submit: SubmitButton = {
    label: submitLabel(
      autoTopUp.isActive,
      selection,
      immediateTopUpChargeCents(data.currentBalanceCents, autoTopUp.settings),
    ),
    isDisabled,
    tooltip: submitTooltip({
      isDisabled,
      isAutoTopUp: autoTopUp.isActive,
      canSaveAutoTopUp: autoTopUp.canSave,
      formTooltip: form.submitTooltip,
    }),
  };

  const submitOneOff = () => {
    if (amountError != null) return;
    if (firstBillingError(form.fieldErrors) != null) {
      card.showMissingBillingDetails();
      return;
    }
    if (!form.isCardComplete) {
      card.showIncompleteCard();
      return;
    }
    form.onSubmit();
  };

  return {
    form,
    selection,
    amountError,
    cardError: card.cardError,
    cardEntry: card.cardEntry,
    autoTopUp,
    isSubmitting,
    submit,
    onSelectionChange: (next: AmountSelection) => {
      setSelection(next);
      applySelection(form, next);
    },
    onCustomBlur: form.onQuantityBlur,
    onSubmit: () => {
      if (isDisabled) return;
      if (!autoTopUp.isActive) {
        submitOneOff();
        return;
      }
      if (hasAutoTopUpErrors(autoTopUp.errors)) return;
      void autoTopUp.save();
    },
  };
}
